'use client'

import { cn } from 'lib/utils'
import { Check } from 'lucide-react'

interface Step {
  id: number
  title: string
  component: any
}

interface StepNavigationProps {
  steps: Step[]
  currentStep: number
  completedSteps: number[]
  onStepChange?: (stepId: number) => void
  mobile?: boolean
}

export function StepNavigation({
  steps,
  currentStep,
  completedSteps,
  onStepChange,
  mobile = false,
}: StepNavigationProps) {
  if (mobile) {
    const currentStepData = steps[currentStep - 1]
    const displayStep =
      currentStep > 0 && currentStep <= steps.length
        ? currentStep
        : steps.length
    const displayTitle =
      currentStepData?.title || steps[steps.length - 1]?.title || 'Complete'

    return (
      <div className="bg-neutral-white rounded-lg border border-neutral-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-neutral-700">
            Step {displayStep} of {steps.length}
          </span>
          <span className="text-sm text-neutral-500">{displayTitle}</span>
        </div>
        <div className="w-full bg-neutral-200 rounded-full h-2">
          <div
            className="bg-brand-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${(displayStep / steps.length) * 100}%` }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full bg-transparent">
      <div className="border-l border-neutral-200 pl-3 border-none">
        <nav className="space-y-3">
          {steps.map((step) => {
            const isCompleted = completedSteps.includes(step.id)
            const isCurrent = currentStep === step.id
            const isUpcoming = step.id > currentStep && !isCompleted

            return (
              <div
                key={step.id}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors rounded-md select-none',
                  isUpcoming && 'opacity-60',
                )}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={
                  isCompleted ? `${step.title} - Completed` : step.title
                }
              >
                <div className="flex items-center justify-center min-w-[28px] w-7 h-7">
                  {isCompleted ? (
                    <div className="rounded-full border w-7 h-7 flex items-center justify-center bg-neutral-100">
                      <Check className="w-4 h-4 text-neutral-600" />
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'rounded-full flex items-center justify-center border text-base w-7 h-7 flex-shrink-0 font-normal',
                        isCurrent &&
                          'border-brand-primary text-brand-primary border',
                        isUpcoming && 'border-neutral-300 text-neutral-400',
                        !isCurrent &&
                          !isUpcoming &&
                          'border-neutral-300 text-neutral-600',
                      )}
                    >
                      {step.id}
                    </div>
                  )}
                </div>
                <span
                  className={cn(
                    'whitespace-normal break-words text-base leading-tight',
                    isCurrent && 'text-brand-primary font-semibold',
                    isCompleted && 'text-neutral-600',
                    isUpcoming && 'text-neutral-400',
                  )}
                >
                  {step.title}
                </span>
              </div>
            )
          })}
        </nav>
      </div>
    </div>
  )
}
